import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { toast } from 'react-toastify';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import {
  fetchAllParticipationCancellationRequests,
  fetchParticipationCancellationRequestById,
} from '../../controllers/participationCancellationController';
import { fetchAllEvents } from '../../controllers/clubEventsController';
import {
  ParticipationCancellationStatus,
  ParticipationCancellationStatusNames,
  buildRequestScopeSummary,
  getParticipationCancellationStatusChipSx,
} from './participationCancellationConstants';
import ApproveParticipationCancellationDialog from './components/ApproveParticipationCancellationDialog';
import RejectParticipationCancellationDialog from './components/RejectParticipationCancellationDialog';

const cardSx = {
  border: '1px solid #e7e7e7',
  boxShadow: 'none',
  borderRadius: '8px',
};

const tableHeadCellSx = {
  fontSize: '14px',
  fontWeight: 500,
  borderBottom: '1px dashed #e8e8e8',
  px: 1.5,
  py: 1.5,
  color: '#a3a3a3',
};

const tableBodyCellSx = {
  px: 1.5,
  py: 1.5,
  color: '#444444',
  fontSize: '14px',
  fontWeight: 500,
};

const actionIconButtonSx = {
  view: {
    backgroundColor: '#E7EDF4',
    borderRadius: '6px',
    width: 32,
    height: 32,
    '&:hover': { backgroundColor: '#CFDBEA' },
    '& svg': { fontSize: '16px', color: '#0E4D94' },
  },
  approve: {
    backgroundColor: '#DFFFEA',
    borderRadius: '6px',
    width: 32,
    height: 32,
    '&:hover': { backgroundColor: '#C5F5D6' },
    '& svg': { fontSize: '16px', color: '#17C653' },
  },
  reject: {
    backgroundColor: '#FFEEF3',
    borderRadius: '6px',
    width: 32,
    height: 32,
    '&:hover': { backgroundColor: '#FFD6E0' },
    '& svg': { fontSize: '16px', color: '#F8285A' },
  },
};

const textFieldSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '5px' },
  '& .MuiInputBase-input': { py: 1, px: 1.5, fontSize: '14px' },
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('tr-TR');
};

const SECTION_PAPER_SX = {
  p: 2,
  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
  border: '1px solid #f5f5f5',
  borderRadius: '8px',
};

const DETAIL_VALUE_SX = {
  fontSize: '14px',
  color: '#444444',
  fontWeight: 500,
  lineHeight: 1.6,
};

const DetailField = ({ label, children }) => (
  <Box>
    <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#a3a3a3' }}>
      {label}
    </Typography>
    {children}
  </Box>
);

const ParticipationCancellationRequests = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  const [status, setStatus] = useState(() => searchParams.get('status') ?? String(ParticipationCancellationStatus.Pending));
  const [eventId, setEventId] = useState(() => searchParams.get('eventId') ?? '');
  const [events, setEvents] = useState([]);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const eventTitleById = useMemo(() => {
    const map = new Map();
    events.forEach((ev) => {
      if (ev?.id) {
        map.set(ev.id, ev.title || ev.name || '');
      }
    });
    return map;
  }, [events]);

  const resolveEventTitle = useCallback(({ eventId, eventName, eventTitle } = {}) => {
    if (eventName) return eventName;
    if (eventTitle) return eventTitle;
    if (eventId && eventTitleById.get(eventId)) return eventTitleById.get(eventId);
    return '-';
  }, [eventTitleById]);

  const loadEvents = useCallback(async () => {
    try {
      const { data } = await fetchAllEvents({ pageNumber: 1, pageSize: 200 });
      setEvents(Array.isArray(data) ? data : []);
    } catch {
      setEvents([]);
    }
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const { data, meta } = await fetchAllParticipationCancellationRequests({
        pageNumber: page,
        pageSize,
        status: status !== '' ? Number(status) : null,
        eventId: eventId || null,
      });
      setRows(Array.isArray(data) ? data : []);
      const total = meta?.totalCount ?? data?.length ?? 0;
      setTotalPages(meta?.totalPages ?? Math.max(1, Math.ceil(total / pageSize)));
    } catch (e) {
      setRows([]);
      setTotalPages(1);
      setLoadError(e?.message || 'Talepler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, status, eventId]);

  const openDetail = useCallback(async (row) => {
    if (!row?.id) return;
    setSelectedRow(row);
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const data = await fetchParticipationCancellationRequestById(row.id);
      setDetail(data);
    } catch (e) {
      toast.error(e?.message || 'Detay alınamadı');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    const requestId = searchParams.get('requestId');
    if (!requestId) return;
    openDetail({ id: requestId });
    const next = new URLSearchParams(searchParams);
    next.delete('requestId');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, openDetail]);

  useEffect(() => {
    const urlStatus = searchParams.get('status');
    const urlEventId = searchParams.get('eventId');
    if (urlStatus != null && urlStatus !== status) setStatus(urlStatus);
    if (urlEventId != null && urlEventId !== eventId) setEventId(urlEventId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = () => {
    loadRows();
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setDetail(null);
  };

  const isPending = (row) => Number(row?.status) === ParticipationCancellationStatus.Pending;

  return (
    <Box>
      <Typography sx={{ fontSize: 18, fontWeight: 500, color: '#444444', mb: 2 }}>
        Katılım İptal Talepleri
      </Typography>

      <Alert severity="info" sx={{ mb: 2, fontSize: '13px' }}>
        Üyeler iptal talebi oluşturduğunda katılım değişmez; admin onayı sonrası iptal ve isteğe bağlı ödeme
        satırı iptali uygulanır. Acil müdahale için etkinlik detayındaki katılım dropdown&apos;u kullanılabilir.
      </Alert>

      <Card sx={{ mb: 2, ...cardSx }}>
        <CardContent sx={{ py: 2, px: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap">
            <Box sx={{ minWidth: 180 }}>
              <Typography sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px' }}>Durum</Typography>
              <TextField
                select
                size="small"
                fullWidth
                value={status}
                onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                sx={textFieldSx}
              >
                <MenuItem value="">Tümü</MenuItem>
                {Object.entries(ParticipationCancellationStatusNames).map(([v, label]) => (
                  <MenuItem key={v} value={v}>{label}</MenuItem>
                ))}
              </TextField>
            </Box>
            <Box sx={{ minWidth: 220, flex: 1 }}>
              <Typography sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px' }}>Etkinlik</Typography>
              <TextField
                select
                size="small"
                fullWidth
                value={eventId}
                onChange={(e) => { setEventId(e.target.value); setPage(1); }}
                sx={textFieldSx}
              >
                <MenuItem value="">Tümü</MenuItem>
                {events.map((ev) => (
                  <MenuItem key={ev.id} value={ev.id}>{ev.title || ev.name || 'İsimsiz etkinlik'}</MenuItem>
                ))}
              </TextField>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>
      )}

      <TableContainer
        component={Paper}
        sx={{
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
          border: '1px solid #f5f5f5',
          py: 1,
          px: 2.5,
        }}
      >
        <Table size="small">
          <TableHead sx={{ bgcolor: 'transparent' }}>
            <TableRow>
              <TableCell sx={tableHeadCellSx}>Talep Tarihi</TableCell>
              <TableCell sx={tableHeadCellSx}>Etkinlik</TableCell>
              <TableCell sx={tableHeadCellSx}>Talep Eden</TableCell>
              <TableCell sx={tableHeadCellSx}>Kapsam</TableCell>
              <TableCell sx={tableHeadCellSx}>Durum</TableCell>
              <TableCell align="right" sx={tableHeadCellSx}>İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow
                  key={`skeleton-${index}`}
                  sx={{
                    '& .MuiTableCell-root': {
                      ...tableBodyCellSx,
                      borderBottom: index === 4 ? 'none' : '1px dashed #e8e8e8',
                    },
                  }}
                >
                  <TableCell><Skeleton width={140} height={20} /></TableCell>
                  <TableCell><Skeleton width={180} height={20} /></TableCell>
                  <TableCell><Skeleton width={120} height={20} /></TableCell>
                  <TableCell><Skeleton width={100} height={20} /></TableCell>
                  <TableCell><Skeleton width={90} height={24} borderRadius={1} /></TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Skeleton width={32} height={32} borderRadius={6} />
                      <Skeleton width={32} height={32} borderRadius={6} />
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                  <Typography sx={{ color: '#a3a3a3', fontSize: '14px' }}>
                    Talep bulunamadı
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => {
                const chipSx = getParticipationCancellationStatusChipSx(row.status);
                return (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{
                      cursor: 'pointer',
                      '& .MuiTableCell-root': {
                        ...tableBodyCellSx,
                        borderBottom: index === rows.length - 1 ? 'none' : '1px dashed #e8e8e8',
                      },
                      '&:hover': { backgroundColor: '#fafafa' },
                    }}
                    onClick={() => openDetail(row)}
                  >
                    <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatDateTime(row.createdAt)}
                    </TableCell>
                    <TableCell>{resolveEventTitle(row)}</TableCell>
                    <TableCell>{row.requesterFullName || row.requesterName || '-'}</TableCell>
                    <TableCell>{buildRequestScopeSummary(row)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.statusName || ParticipationCancellationStatusNames[row.status]}
                        sx={{
                          height: 24,
                          fontSize: '12px',
                          fontWeight: 600,
                          ...chipSx,
                        }}
                      />
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Detay">
                          <IconButton
                            size="small"
                            onClick={() => openDetail(row)}
                            sx={actionIconButtonSx.view}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        {isPending(row) && (
                          <>
                            <Tooltip title="Onayla">
                              <IconButton
                                size="small"
                                onClick={() => { setSelectedRow(row); setApproveOpen(true); }}
                                sx={actionIconButtonSx.approve}
                              >
                                <CheckCircleIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reddet">
                              <IconButton
                                size="small"
                                onClick={() => { setSelectedRow(row); setRejectOpen(true); }}
                                sx={actionIconButtonSx.reject}
                              >
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} size="small" />
        </Box>
      )}

      <Dialog
        open={detailOpen}
        onClose={handleCloseDetail}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          İptal Talebi Detay
          <IconButton
            type="button"
            onClick={handleCloseDetail}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} sx={{ color: '#0E4D94' }} />
            </Box>
          ) : detail ? (
            <Stack spacing={2.5}>
              <Paper sx={SECTION_PAPER_SX}>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <DetailField label="Etkinlik">
                      {detail.eventId ? (
                        <Link
                          component={RouterLink}
                          to={`/events/${detail.eventId}`}
                          sx={{
                            ...DETAIL_VALUE_SX,
                            color: '#0E4D94',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          {resolveEventTitle(detail)}
                        </Link>
                      ) : (
                        <Typography sx={DETAIL_VALUE_SX}>{resolveEventTitle(detail)}</Typography>
                      )}
                    </DetailField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DetailField label="Talep eden">
                      <Typography sx={DETAIL_VALUE_SX}>{detail.requesterFullName || '-'}</Typography>
                    </DetailField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DetailField label="Talep tarihi">
                      <Typography sx={{ ...DETAIL_VALUE_SX, fontVariantNumeric: 'tabular-nums' }}>
                        {formatDateTime(detail.createdAt)}
                      </Typography>
                    </DetailField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DetailField label="Durum">
                      <Chip
                        size="small"
                        label={detail.statusName || ParticipationCancellationStatusNames[detail.status]}
                        sx={{
                          height: 24,
                          fontSize: '12px',
                          fontWeight: 600,
                          ...getParticipationCancellationStatusChipSx(detail.status),
                        }}
                      />
                    </DetailField>
                  </Grid>
                  <Grid item xs={12}>
                    <DetailField label="Kapsam">
                      <Typography sx={DETAIL_VALUE_SX}>{buildRequestScopeSummary(detail)}</Typography>
                    </DetailField>
                  </Grid>
                </Grid>
              </Paper>

              {detail.reason && (
                <Paper sx={SECTION_PAPER_SX}>
                  <DetailField label="Talep açıklaması">
                    <Typography sx={{ ...DETAIL_VALUE_SX, whiteSpace: 'pre-wrap' }}>{detail.reason}</Typography>
                  </DetailField>
                </Paper>
              )}

              {detail.rejectionReason && (
                <Paper sx={SECTION_PAPER_SX}>
                  <DetailField label="Red sebebi">
                    <Typography sx={{ ...DETAIL_VALUE_SX, whiteSpace: 'pre-wrap' }}>{detail.rejectionReason}</Typography>
                  </DetailField>
                </Paper>
              )}

              {detail.adminNotes && (
                <Paper sx={SECTION_PAPER_SX}>
                  <DetailField label="Admin notları">
                    <Typography sx={{ ...DETAIL_VALUE_SX, whiteSpace: 'pre-wrap' }}>{detail.adminNotes}</Typography>
                  </DetailField>
                </Paper>
              )}
            </Stack>
          ) : (
            <Alert severity="error" sx={{ borderRadius: '5px' }}>Detay yüklenemedi</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1 }}>
          {detail && isPending(detail) && (
            <>
              <Box sx={{ flex: 1 }} />
              <Button
                type="button"
                variant="outlined"
                onClick={() => {
                  handleCloseDetail();
                  setRejectOpen(true);
                  setSelectedRow(detail);
                }}
                sx={{
                  textTransform: 'none',
                  fontSize: '13px',
                  borderRadius: '5px',
                  borderColor: '#F8285A',
                  color: '#F8285A',
                  '&:hover': {
                    borderColor: '#F8285A',
                    backgroundColor: '#FFEEF3',
                  },
                }}
              >
                Reddet
              </Button>
              <Button
                type="button"
                variant="contained"
                onClick={() => {
                  handleCloseDetail();
                  setApproveOpen(true);
                  setSelectedRow(detail);
                }}
                sx={{
                  textTransform: 'none',
                  fontSize: '13px',
                  backgroundColor: '#17C653',
                  borderRadius: '5px',
                  px: 2,
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#14a847',
                    boxShadow: 'none',
                  },
                }}
              >
                Onayla
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <ApproveParticipationCancellationDialog
        open={approveOpen}
        onClose={() => setApproveOpen(false)}
        requestId={selectedRow?.id}
        onApproved={handleRefresh}
      />

      <RejectParticipationCancellationDialog
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        requestId={selectedRow?.id}
        onRejected={handleRefresh}
      />
    </Box>
  );
};

export default ParticipationCancellationRequests;
